import { useEffect, useState } from 'react';
import { setNavFeedback } from '../nav/engine.ts';
import { audio, type AudioPrefs, type Sfx } from './engine.ts';

export { audio };
export type { AudioPrefs, Sfx };

export function useAudioPrefs(): [AudioPrefs, (p: Partial<AudioPrefs>) => void] {
  const [prefs, setPrefs] = useState(audio.prefs);
  useEffect(() => {
    const off = audio.onChange(setPrefs);
    return () => void off();
  }, []);
  return [prefs, (p) => audio.setPrefs(p)];
}

/** Silence REPLAY while mounted (used by the game player). */
export function useSilence(on = true) {
  useEffect(() => {
    if (!on) return;
    audio.suppress(true);
    return () => audio.suppress(false);
  }, [on]);
}

/**
 * Wires the sound identity to the app: unlocks audio on the first real user
 * gesture (browsers forbid autoplay before that), and turns navigation,
 * clicks and taps into interface sounds.
 */
export function AudioRoot() {
  useEffect(() => {
    // Autoplay policy: only a genuine gesture may start audio.
    const unlock = () => audio.unlock();
    const opts = { capture: true, passive: true } as const;
    window.addEventListener('pointerdown', unlock, opts);
    window.addEventListener('keydown', unlock, opts);
    window.addEventListener('touchend', unlock, opts);

    setNavFeedback((f) => {
      // Gamepad presses count as activation in most browsers; try anyway.
      if (f.source === 'gamepad') audio.unlock();
      switch (f.action) {
        case 'up':
        case 'down':
        case 'left':
        case 'right':
          if (f.moved) audio.play('move', { intensity: f.repeat ? 0.55 : 1 });
          break;
        case 'accept':
          audio.play('confirm');
          break;
        case 'back':
          audio.play('back');
          break;
        case 'prevTab':
        case 'nextTab':
          audio.play('tab');
          break;
        case 'tertiary':
          if (f.by !== 'default') audio.play('toggle');
          break;
        case 'secondary':
          if (f.by !== 'default') audio.play('menuOpen');
          break;
      }
    });

    // Mouse and touch: a soft confirm on activating controls.
    const onClick = (e: MouseEvent) => {
      if (e.detail === 0) return; // keyboard/gamepad-generated click: already handled
      const el = (e.target as HTMLElement | null)?.closest?.('a[href], button, [role="button"], .chip, input[type="checkbox"]');
      if (!el || (el as HTMLButtonElement).disabled || el.closest('[data-sfx-off]')) return;
      audio.play(el.matches('input[type="checkbox"], .chip, [aria-pressed]') ? 'toggle' : 'confirm');
    };
    document.addEventListener('click', onClick, true);

    return () => {
      window.removeEventListener('pointerdown', unlock, opts);
      window.removeEventListener('keydown', unlock, opts);
      window.removeEventListener('touchend', unlock, opts);
      document.removeEventListener('click', onClick, true);
      setNavFeedback(null);
    };
  }, []);
  return null;
}
